<!-- eslint-disable -->

<template>
  <DataTable
    id="quotesDataTable"
    v-model:selection="selectedLineItem"
    v-model:expandedRows="expandedRows"
    :value="quote?.quote_line_items"
    tableStyle="min-width: 100%; border: black 2px solid;"
    rowGroupMode="subheader"
    groupRowsBy="parent_order"
    sortField="sub_order"
    sortMode="single"
    :sortOrder="1"
    class="mb-5"
    :selectionMode="quoteIsWonOrRejected ? undefined : 'single'"
    dataKey="id"
    :loading="loading"
    :pt="{
      bodyRow: (options) => ({ id: options?.props?.rowData?.id }),
      rowGroupHeaderCell: { colSpan: 10 },
    }"
    @row-reorder="quoteIsWonOrRejected ? null : onRowReorder($event)"
    @row-select="onRowSelect"
    @row-unselect="onRowUnselect"
  >
    <template #header>
      <h2 class="mb-0 mt-0">QUOTE DESIGN LIST</h2>
    </template>

    <template #groupheader="quote_line_items">
      <!-- SUBHEADER -->
      <span
        class="subheader vertical-align-middle font-bold line-height-3 ml-2"
      >
        {{
          getLineItemTypeLabel(quote_line_items.data.line_item_type)
        }}
      </span>
    </template>

    <template #empty>
      <div class="empty h-full text-center pb-3 mb-2 mt-2">
        <h3>No Line Items</h3>
        <div v-if="sidePanelOpen">
          With the side panel open, you can search and filter for materials to
          add.
          <br /><br />
          Select a material from the dropdown, then click the
          <b>Add to Quote</b> button to add a line item.
        </div>
        <div v-else>
          Add line items to your quote by clicking the right-hand tray.
        </div>
      </div>
    </template>

    <template #loading>
      <div>Your quote data is loading, please wait...</div>
      <div class="ml-2 loading-spinner"></div>
    </template>

    <template #expansion="slotProps">
      <div 
        class="dndPlaceholder" 
        :id="`row-expansion-${slotProps.data.id}`"
      >
        Drop Material Here
      </div>
    </template>

    <Column
      class="data-column h-full pl-1 pr-1"
      headerStyle="width: 0rem;"
      rowReorder
      :reorderableColumn="false"
    >
      <!-- DND: -->
      <template #body="quote_line_items">
        <div
          class="text-center"
          :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
        >
          {{ quote_line_items.data.sub_order * 10 }}
        </div>
        <i
          class="p-datatable-reorderablerow-handle dnd-handle pi pi-bars"
          :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
          data-pc-section="rowreordericon"
        />
      </template>
    </Column>

    <Column
      class="data-column pl-0"
      field="erp_material_number"
      header="MATERIAL NUMBER"
      headerStyle="width: 10.7rem"
    >
      <template #body="quote_line_items">
        <div class="text-center text-xs product-badge mb-1">Product</div>
        <div class="text-nowrap">
          <span>
            <Badge value="MN" class="mn-badge font-bold" />
            <span class="">
              <b class="text-sm pl-1 text-right">{{
                quote_line_items.data.erp_material_number
              }}</b>
            </span>
          </span>
        </div>

        <div class="mt-1" v-if="quote_line_items.data.source == 'change_order'">
          <Badge>CHANGE ORDER</Badge>
        </div>

        <div class="text-sm pl-0 pr-0 font-bold">
          <Button
            v-if="_isJobTypeWellTestRental"
            class="pl-0 pr-0 pt-0 pb-0 mt-2"
            :class="
              isConsumable(quote_line_items.data)
                ? 'consumable-text'
                : 'non-consumable-text'
            "
            :label="
              isConsumable(quote_line_items.data)
                ? 'Consumable'
                : 'Non-consumable'
            "
            :icon="
              isConsumable(quote_line_items.data) ? 'pi pi-dollar' : 'pi pi-ban'
            "
            :disabled="saving || deleting || quoteIsWonOrRejected"
            link
            @click="toggleSubcategory(quote_line_items.data)"
          />
        </div>
        <div class="pt-0 text-xs" style="vertical-align: bottom">
          <div v-if="quote_line_items.data.editing_note || quote_line_items.data.editing_description" class="flex mt-2">
            <Button
              label="Save"
              class="p-button-sm note-button-save mr-2"
              severity="success"
              :disabled="saving || deleting || quoteIsWonOrRejected"
              @click="quote_line_items.data.editing_note ? saveNote(quote_line_items.data) : saveDescription(quote_line_items.data)"
            />
            <Button
              label="Clear"
              class="p-button-sm note-button-clear"
              severity="danger"
              :disabled="saving || deleting || quoteIsWonOrRejected"
              @click="quote_line_items.data.editing_note ? clearNote(quote_line_items.data) : clearDescription(quote_line_items.data)"
              @mousedown="overrideBlurEvent = true"
              @mouseup="overrideBlurEvent = false"
            />
          </div>
          <div
            v-else
            v-if="!quote_line_items.data.note"
            class="note-button-add-1 mt-2"
          >
            <Button
              :label="'+ Note'"
              class="p-button-sm note-button-add-2"
              :disabled="saving || deleting || quoteIsWonOrRejected"
              link
              @click="toggleNoteEdit(quote_line_items.data)"
            />
          </div>
        </div>
      </template>
    </Column>
    <Column
      class="data-column"
      field="erp_material_long_description"
      header="DESCRIPTION"
    >
      <template #body="quote_line_items">
        <div v-if="!isCustomLineItem(quote_line_items.data)">
          <!-- Normal line items don't need the description edit,
               so just plainly display the long description -->
          <div
            class="flex align-items-center"
            @click="handleCopyButtonClick($event)"
          >
            {{ quote_line_items.data.erp_material_long_description }}
            <Button
              icon="pi pi-copy"
              v-tooltip="'Copy description'"
              class="line-item-button"
              @click="copyDescriptionToClipboard(quote_line_items.data.erp_material_long_description)"/>
          </div>
        </div>
        <div v-else class="flex">
          <!-- We'll need to show slightly different components if the
               line item is custom, so refer to this block

               Note: hide the description button when it gets clicked,
               (even though the Textarea is hiding it). This prevents the
               row selection when the button is clicked. Using the
               $event.stopPropagation() on @mousedown to prevent the
               events from propagating to their parents did not work
               and still caused the row to be selected.
            -->
          <Button
            v-if="!quote_line_items.data.editing_description"
            class="p-button p-button-icon-only line-item-button w-auto"
            :class="!quote_line_items.data.editing_note ? 'link-button' : 'link-button-disabled'"
            @click="!quote_line_items.data.editing_note ? toggleDescriptionEdit(quote_line_items.data) : saveNote(quote_line_items.data)"
            v-tooltip.right="!quote_line_items.data.editing_note ? 'Click to edit this description' : ''"
          >
            <div v-if="!quote_line_items.data.editing_description">
              {{ quote_line_items.data.erp_material_long_description }}
              <!-- Keep the inline style in the <i> component, it's rpeventing
                   the row from being selected (returning from onRowSelect with
                   the below matching element is not sufficient)  -->
              <i v-if="!quote_line_items.data.editing_note" class="pi pi-file-edit pl-1" style="pointer-events: none;"></i>
            </div>
          </Button>
          <Button
            v-if="!quote_line_items.data.editing_description"
            icon="pi pi-copy"
            v-tooltip="'Copy description'"
            class="line-item-button"
            @click="copyDescriptionToClipboard(quote_line_items.data.erp_material_long_description)"/>
          <Textarea
            v-if="quote_line_items.data.editing_description"
            v-model="quote_line_items.data.erp_material_long_description"
            class="w-full"
            rows="4"
            @blur="overrideBlurEvent ? null : saveDescription(quote_line_items.data)"
          />
        </div>

        <!-- Always show the section for note, regardless if custom or regular line item -->
        <Textarea
          v-if="quote_line_items.data.editing_note"
          v-model="quote_line_items.data.note"
          class="w-full"
          rows="4"
          @blur="overrideBlurEvent ? null : saveNote(quote_line_items.data)"
        />
        <button
          v-if="quote_line_items.data.note && !quote_line_items.data.editing_note"
          :class="!quote_line_items.data.editing_description ? 'pt-2 link-button' : 'pt-2 link-button-disabled'"
          @click="!quote_line_items.data.editing_description ? toggleNoteEdit(quote_line_items.data) : saveDescription(quote_line_items.data)"
          v-tooltip.right="!quote_line_items.data.editing_description ? 'Click to edit this note' : ''"
        >
          <b class="pr-1">NOTE:</b> {{ quote_line_items.data.note }}
          <i v-if="!quote_line_items.data.editing_description" class="pi pi-file-edit pl-1"></i>
        </button>
      </template>
    </Column>
    <Column
      class="data-column"
      field="cost"
      header="COST"
      headerStyle="width: 6rem"
    >
      <template #body="quote_line_items">
        <div v-if="!isCustomLineItem(quote_line_items.data)">
          {{formatCurrency(quote_line_items.data.erp_price, quote?.currency)}}
        </div>
      </template>
    </Column>
    <Column
      class="data-column"
      field="pricing_date"
      header="PRICING DATE"
      headerStyle="width: 8.5rem"
    >
      <template #body="quote_line_items">
        <div style="position: relative;">
          <Calendar
            v-if="!isCustomLineItem(quote_line_items.data)"
            v-tooltip="'Click to change pricing date'"
            v-model="quote_line_items.data.pricing_date"
            date-format="mm/dd/yy"
            id="pricing-date"
            :manual-input="true"
            show-icon
            show-button-bar
            link
            :disabled="saving || deleting || quoteIsWonOrRejected"
            @date-select="recalculateUnitPriceAndTotalFromDate(quote_line_items.data)"
            @clear-click=""
          />
          <span
            class="pricing-date-temporary-fix"
            :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
            v-if="!isCustomLineItem(quote_line_items.data)"
          >
            <!-- Dev note: date-format being overriden by save+refetches,
            so this is temporary solution to show SpyGlass date format -->
            {{ getSpyGlassDate(quote_line_items.data.pricing_date) }}
          </span>
        </div>
        <!-- TODO: writing "!important" in an inline style is the only way for the dotted line to show up, fix this -->
      </template>
    </Column>
    <Column
      class="data-column text-left"
      field="margin"
      header="MARGIN"
      headerStyle="width: 5.9rem;"
    >
      <template #body="quote_line_items">
        <Button
          v-if="!isCustomLineItem(quote_line_items.data)"
          v-tooltip="'Click to show price history for this line item'"
          class="icon-height-fix"
          icon="pi pi-chart-line p-button-sm"
          :disabled="saving || deleting || quoteIsWonOrRejected || showRequiredInfoMessage"
          link
          @click="onShowPriceHistory(quote_line_items.data)"
        />
        <span
          v-if="!isCustomLineItem(quote_line_items.data)"
          class="input-symbol-percentage"
          :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
        >
          <input
            v-if="!isCustomLineItem(quote_line_items.data)"
            v-model="quote_line_items.data.margin"

            class="w-full p-inputtext"
            :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
            type="number"
            step="0.001"
            :disabled="saving || deleting || quoteIsWonOrRejected"
            placeholder="0"
            @change="recalcPriceOverrideFromMargin($event, quote_line_items.data)"
          />
        </span>
      </template>
    </Column>
    <Column
      class="data-column text-left"
      field="erp_price"
      header="UNIT PRICE"
      headerStyle="width: 8rem"
    >
      <template #body="quote_line_items">

        <span
          class="input-symbol-usd"
          :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
        >
          <!-- Changes total -->
          <input
            :id="quote_line_items.data.id"
            v-model="quote_line_items.data.price_override"
            class="w-full p-inputtext"
            type="number"
            placeholder="0.00"
            name="currency"
            :disabled="saving || deleting || quoteIsWonOrRejected"
            @change="recalcMarginFromPriceOverride($event, quote_line_items.data)"
          />
        </span>
      </template>
    </Column>
    <Column
      class="data-column text-left"
      field="quantity"
      header="QTY"
      headerStyle="width: 6rem;"
    >
      <template #body="quote_line_items">
        <InputText
          v-model="quote_line_items.data.quantity"
          class="w-full"
          :class="{
            'p-disabled': saving || deleting || quoteIsWonOrRejected,
            'p-invalid': quote_line_items.data.quantity <= 0
          }"
          placeholder="0"
          type="text"
          :disabled="saving || deleting || quoteIsWonOrRejected || isSerialized(quote_line_items.data)"
          showButtons
          autocomplete="off"
          @keydown="checkValidNumberInput($event, false, false)"
          @paste="sanitizePastedNumberInput"
        />
      </template>
    </Column>
    <Column
      class="data-column text-left"
      field="total"
      header="TOTAL"
      headerStyle="width: 8rem"
    >
      <template #body="quote_line_items">
        <span class="input-symbol-usd">
          <input
            class="w-full p-inputtext"
            :class="{ 'p-disabled': saving || deleting || quoteIsWonOrRejected }"
            placeholder="0.00"
            type="number"
            toggleSavingOrLoading
            showButtons
            :value="
              (getQuoteLineUnitPrice(quote_line_items.data) *
                quote_line_items.data.quantity).toFixed(2) || ''
            "
            @change="recalcPriceOverrideAndMarginFromTotal($event, quote_line_items.data)"
          />
          <!-- Note: if you want to modify this total input, keep in mind that
               you will encounter several limitations with :value (instead of
               v-model) and inconsistent focus states (when saving/refetching) -->
          <!-- Dev note: do not use toFixed in :value unless you want jank
               UI / UX results -->
          <!-- TODO: the more I worked with :value, I'm thinking that v-model
               is the right play here -->
        </span>
      </template>
    </Column>
    <Column
      field="trash"
      class="data-column text-center pl-0 vertical-align-bottom"
      headerStyle="width: 1rem"
    >
      <template #body="quote_line_items">
        <Button
          class="mb-5"
          :label="
              isFree(quote_line_items.data)
                ? 'Free'
                : 'Not Free'
            "
          :disabled="saving || deleting || quoteIsWonOrRejected"
          link
          @click="toggleFree(quote_line_items.data)"
        />
        <Button
          v-tooltip.left="'Remove this line item'"
          class="trash-icon"
          icon="pi pi-trash"
          link
          :disabled="saving || deleting || quoteIsWonOrRejected"
          @click="removeLineItem($event, quote_line_items.data)"
        />
        <div v-if="deleting" class="loading-spinner"></div>
      </template>
    </Column>
  </DataTable>
</template>

<script lang="ts" setup>
/* eslint-disable */
// @ts-nocheck

import { useOnline } from "@vueuse/core";
import { haltUntilCompletion } from "@/composables/exchanges/mutationLimiter";
import {
  computed, ref, onMounted,
  toRefs, watch, nextTick,
  toRaw, reactive, PropType
} from "vue";
import { useRouter } from "vue-router";
import { getQuoteLineUnitPrice, isQuoteAdjustmentLineItem } from "@/utils/quotePricing";
import type {
  DataTableRowReorderEvent,
  DataTableRowSelectEvent
} from "primevue/datatable";
import DataTable from "primevue/datatable";
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import { useToast } from "primevue/usetoast";
import {
  type GetQuoteByPkQuery,
  type Quotes,
  AllQuoteFieldsFragment as QuoteType,
  useInsertQuoteLineItemMutation,
  useUpdateQuoteLineItemByPkMutation,
  useUpdateManyQuoteLineItemsMutation,
  type SkMaterial,
  useSkMaterialsQuery,
  useSkEquipmentTypeConversionsQuery,
  useUpdateQuoteByPkMutation,
  Quote_Line_Item_Category_Dataset_Enum as Category,
  Quote_Line_Item_Subcategory_Dataset_Enum as SubCategory
} from "gql/api";
import { storeToRefs } from "pinia";

import {
  sidePanelActions as SidePanelActions,
  MOTOR_LEAD_EXTENSION,
  SEAL,
  parentOrder,
  statuses,
  formatCurrency,
  fixDate,
  formatQuoteHeaderForSave,
  formatQuoteLineItemForSave,
  exclude_fields,
  createQueryString,
  getDefaultMargin,
  getLineItemTypeLabel,
  getSpyGlassDate,
  checkValidNumberInput,
  sanitizePastedNumberInput,
  LineItemType,
  getSubcategoryBySerialized,
  isQuantityOneMaterial,
  getAddedMaterialSubOrder,
  isJobTypeWellTestRental,
  isMissingRequiredFields
} from "@/utils/quoteHelpers";
import { useLineItemStore } from "@/store/line_item_store";
import { useSidePanelStore } from "@/store/side_panel";

const globalDebounceTime = 2000;

const toast = useToast();

// ROUTE STUFF:
const router = useRouter();
const quoteIdRouter = router.currentRoute?.value?.params?.quoteId;

const {
  addNewQuoteLineItem,
  setQuoteLineItems,
  addSelectedDescription,
  removeSelectedDescription,
  addSelectedQuoteLineItem,
  removeSelectedQuoteLineItem,
} = useLineItemStore();

// set data
const { setUserManualSearch, setAddQuantity, resetAddQuantity } = useSidePanelStore();
// get data
const { userManualSearch, addQuantity } = storeToRefs(useSidePanelStore());

const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType | null>,
    default: null
  },
  currency: {
    type: String,
    default: null
  },
  refetchQuote: {
    type: Function,
    default: () => {}
  },
  sidePanelOpen: {
    type: Boolean,
    default: false
  },
  advancedSearch: {
    type: Object as PropType<QuotesSearch>,
    default: null
  },
  selectedMaterialRef: {
    type: Object,
    default: null
  },
  selectedActionRef: {
    type: Object,
    default: ""
  }
});
// Object as PropType<ref<SkMaterial>>

// REFS:
const { quote, selectedMaterialRef, selectedActionRef, advancedSearch } =
  toRefs(props);
const saving = ref<boolean>(false);
const selectedLineItem = ref<Object>(null);
const selectedLineItemElement = ref<HTMLTableRowElement>();
const loading = ref<boolean>(false); // TODO: not needed?
const expandedRows = ref({});
const overrideBlurEvent = ref<boolean>(false);
const showedPriceDateWarnToast = ref<boolean>(false);

// MUTATIONS:
const { executeMutation: updateQuoteLineItem } =
  useUpdateQuoteLineItemByPkMutation();
const { executeMutation: insertQuoteLineItem } =
  useInsertQuoteLineItemMutation();
const { executeMutation: updateManyQuoteLineItems } =
  useUpdateManyQuoteLineItemsMutation();
const { executeMutation: updateQuote } =
  useUpdateQuoteByPkMutation();


// EMITS
const emit = defineEmits([
  "update:open-side-panel",
  "toggle",
  "description-selected"
]);

const isFree = (qli: any) => {
  return qli.tax_type === "free";
};

const toggleFree = (qli: any) => {
  qli.tax_type = isFree(qli) ? "taxed" : "free";
};

// QUOTE ACTION HELPERS:
watch(selectedMaterialRef, (newMat, oldMat) => {
  // DEV NOTE: an old computed and watcher is merged into a better watcher.
  // The following will probably be removed/reworked once Pinia is implemented.

  const material = selectedMaterialRef?.value;

  if (!material) {
    // selected material is empty
    return {};
  }

  const quoteLineItem = {};

  // Translate the material from ERP to quote / spyglass
  // TODO: possible candidate for helper function?
  quoteLineItem.quote_id = quote?.value?.id;
  quoteLineItem.erp_material_long_description =
    material?.long_description || material?.description || "";
  quoteLineItem.erp_material_number = material?.number || "";
  quoteLineItem.erp_material_short_description = material?.description || "";
  quoteLineItem.erp_price = material?.pricing?.price || 0;
  quoteLineItem.price_override = material?.pricing?.price || material?.custom_price || 0; // Default to erp_price for both adding and replacing
  quoteLineItem.erp_product_hierarchy = material?.prod_hierarchy || "";
  quoteLineItem.erp_uom = material?.base_uom || "FT";
  quoteLineItem.line_item_type = material?.line_item_type || LineItemType.UNCATEGORIZED;
  quoteLineItem.parent_order = parentOrder[quoteLineItem.line_item_type];
  // convert from boolean to erp_serialied enum string here
  quoteLineItem.erp_serialized = material?.serialized ? "serialized" : "unserialized";

  if (quoteLineItem.line_item_type === "---") {
    // The --- is for a break in the dropdown
    quoteLineItem.line_item_type = LineItemType.UNCATEGORIZED
  }

  const replacing =
    selectedActionRef.value.selected == SidePanelActions.replace;

  quoteLineItem.category = replacing
    ? selectedLineItem.value.category
    : material?.material_type === "ZSER"
      ? Category.Service
      : material?.category || Category.Equipment;
  quoteLineItem.credit = replacing ? selectedLineItem.value.credit : 0;
  quoteLineItem.from_contract = newMat.from_contract;
  quoteLineItem.margin = replacing
    ? selectedLineItem.value.margin
    : getDefaultMargin(quoteLineItem)
  delete quoteLineItem.from_contract;

  if (quoteLineItem.margin && quoteLineItem.margin > 0) {
    recalcPriceOverrideFromMargin(null, quoteLineItem, false, false);
  }

  quoteLineItem.note = replacing ? selectedLineItem.value.note : "";
  quoteLineItem.quantity = material.quantity || 1;
  quoteLineItem.source = replacing ? selectedLineItem.value.source : "user";
  quoteLineItem.subcategory = replacing
    ? selectedLineItem.value.subcategory
    : getSubcategoryBySerialized(material?.erp_serialized);
  quoteLineItem.sub_order = replacing
    ? selectedLineItem.value.sub_order
    : getAddedMaterialSubOrder(material?.line_item_type, quote?.value);
  quoteLineItem.tax_type = replacing
    ? selectedLineItem.value.tax_type
    : "taxed";
  quoteLineItem.pricing_date = replacing
    ? selectedLineItem.value.pricing_date
    : fixDate(advancedSearch?.value?.pricingDate);
  quoteLineItem.spyglass_equipment_import_json = replacing
    ? selectedLineItem.value.spyglass_equipment_import_json
    : "";

  // Finally process the action(s) committed by the user:
  const actions = SidePanelActions;
  const equipmentType1 = selectedLineItem?.value?.line_item_type;
  const equipmentType2 = quoteLineItem?.line_item_type;

  if (quote?.value?.id) {
    saving.value = true;
    emit("toggle", saving.value);
    let userActionPromise: Promise<
      Error | Promise<OperationResult<UpdateQuoteByPkMutation, AnyVariables>>
    >;
    switch (selectedActionRef.value.selected) {
      case actions.add:
      case actions.custom:
        userActionPromise = addLineItemToQuote(quoteLineItem);
        break;
      case actions.replace:
        if (equipmentType1 == equipmentType2) {
          userActionPromise = replaceLineItem(quoteLineItem);
        } else {
          userActionPromise = replaceLineItemTypeMismatch(quoteLineItem);
        }
        break;
      default:
        userActionPromise = Promise.reject(new Error('Case not found for user selected action: ', selectedActionRef.value.selected));
        break;
    }

    userActionPromise
      .then((resp) => {
        props.refetchQuote().finally(() => {
          applyDragoverFunctions();
          selectedLineItem.value = null;
          saving.value = false;
          emit("toggle", saving.value);
        });
      }).catch((err) => {
        console.error("There was an issue resolving the user action: ", selectedActionRef.value.selected, "\n", err)
      })
  } else if (!quote?.value?.id) {
    // TODO: possible candidate for removal, investigate
    // also need to reset action when no quote to avoid multiple inserts
    selectedActionRef.value.selected = actions.none;

    // add to local list
    // TODO: this variable doesn't exist
    newQuoteLineItems.value.push(newLineItem);

    // add to store
    addNewQuoteLineItem(newLineItem);
    selectedLineItem.value = null;
    saving.value = false;
    emit("toggle", saving.value);
  }
})

const quote_line_items = computed(() => {
  return [...(quote?.value?.quote_line_items || [])];
});

//const quote_line_items = computed({
//  get() {
//    return [...(quote?.value?.quote_line_items || [])];
//  },
//  set(newVal) {
//    if (quote.value) {
//      quote.value.quote_line_items = newVal;
//    }
//  }
//});

const showRequiredInfoMessage = computed(() =>
  isMissingRequiredFields(advancedSearch)
);

const addLineItemToQuote = (newLineItem) => {
  const quoteObj = formatQuoteHeaderForSave(quote?.value, advancedSearch.value)

  const createdLineItems = [];

  const incrementQliSubordersFromQli = (line_item: any) => {
    quote?.value?.quote_line_items
      .filter(qli => {
        if (qli.sub_order && qli?.sub_order >= line_item.sub_order) {
          qli.sub_order += 1;
        }
      })
  };

  if (isSerialized(newLineItem)) {
    const indexList = [...Array(addQuantity.value).keys()];

    const suborder_start = getAddedMaterialSubOrder(newLineItem.line_item_type, quote?.value);
    let calculated_sub_order = suborder_start;

    indexList.forEach((idx) => {
      newLineItem.sub_order = calculated_sub_order;

      createdLineItems.push(formatQuoteLineItemForSave(newLineItem, quote?.value?.id));

      calculated_sub_order += 1;

      /* Looking at O(m*n) complexity with the following nested loop.

      Should be fine as long as our max selectable quantity is 49 (n), and the max
      theoretical line items before adding serialized quantity items is 11 (m).

      Justification: The max number of line items I've seen in production is 60.
      So 11 pre-add, and 49 post-add, gives a total max 60 real-world QLIs.

      Therefore, 49*11 = 539 max theoretical client operations.
      Saving/refetching to/from the server takes longer.

      - Lawrence
      **/
      incrementQliSubordersFromQli(newLineItem);
    });
  } else if (!isSerialized(newLineItem)) {
    if (selectedActionRef.value.selected !== SidePanelActions.custom) {
      // TODO: this is being used for regular add?
      newLineItem.quantity = addQuantity.value;
    }

    createdLineItems.push(formatQuoteLineItemForSave(newLineItem, quote?.value?.id));
    incrementQliSubordersFromQli(newLineItem);
  }

  // Finally, capture all line items for update - any could have been edited since adding
  // TODO: later refinement will only save data that was changed over blanket saving
  const updatedLineItems = quote_line_items?.value
    .filter(lineItem => lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
    .map(item => {
      return {
        _set: item,
        where: {
          id: { _eq: item.id }
        }
      };
    });

  return updateQuote({
    id: quote?.value?.id,
    object: quoteObj,
    create_line_items: createdLineItems,
    update_line_items: updatedLineItems,
  })
    .then(resp => {
      if(isSerialized(newLineItem)) {
        const num_added = Number(addQuantity.value)
        if (num_added === 1) {
          toastMaterialAddSuccess(newLineItem);
        }
        else {
          toastMaterialAddSerializedSuccess(
            newLineItem,
            num_added,
            createdLineItems[0].sub_order,
            createdLineItems[createdLineItems.length - 1].sub_order
          );
        }
      }
      else if (!isSerialized(newLineItem)) {
        toastMaterialAddSuccess(newLineItem);
      }

      // Go back to quantity 1 after request is finished
      resetAddQuantity();
    })
    .catch((err) => {
      toastMaterialAddError(newLineItem, err);
      return Promise.reject(new Error(' invalid quote line item insert:\n', err))
    })
};


const replaceLineItem = (newLineItem: any) => {
  selectedLineItem.value.erp_product_hierarchy = newLineItem.erp_product_hierarchy;
  for (const key in newLineItem) {
    if (key == "id") {
      continue;
    }
    if (selectedLineItem.value.hasOwnProperty(key)) {
      selectedLineItem.value[key] = newLineItem[key];
    }
  }

  const quoteObj = formatQuoteHeaderForSave(quote?.value, advancedSearch?.value);

  const createdLineItems = quote.value.quote_line_items
    .filter(lineItem => !lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quoteId));

  const updatedLineItems = quote.value.quote_line_items
    .filter(lineItem => lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
    .map(item => {
      return {
        _set: item,
        where: {
          id: { _eq: item.id }
        }
      };
    });

  return updateQuote({
    id: quote?.value?.id,
    object: quoteObj,
    create_line_items: createdLineItems,
    update_line_items: updatedLineItems,
  }).then((resp) => {
    toastMaterialReplaceSuccess(selectedLineItem.value);
    return resp;
  }).catch((err) => {
    toastMaterialAddError(newLineItem, err);
    return Promise.reject(new Error("Error replacing line item:\n", err))
  })
};

const replaceLineItemTypeMismatch = (newLineItem: any) => {
  // For user informational purposes, copying this before it's overwritten:
  const _selectedLineItemCopy = Object.assign({}, selectedLineItem.value)

  const existing_item_types = quote_line_items.value.filter(
    qli => qli.parent_order === newLineItem.parent_order
  );

  let sub_order = 0;

  if (existing_item_types.length) {
    sub_order =
      existing_item_types[existing_item_types.length - 1].sub_order + 1;
  }

  newLineItem.sub_order = sub_order;

  Object.assign(
    quote_line_items.value.find(
      qli => qli.id === selectedLineItem.value.id
    ),
    newLineItem
  );

  // Sort the list by parent_order and then by sub_order
  quote_line_items.value
    .sort((a, b) => {
      if (a.parent_order === b.parent_order) {
        return a.sub_order - b.sub_order;
      }
      return a.parent_order - b.parent_order;
    })
    .forEach(qli => {
      qli.sub_order = quote_line_items.value.indexOf(qli) + 1;
    });

  const updated_line_item = quote_line_items.value.find(
    qli => qli.id === selectedLineItem.value.id
  );

  const qlis = quote?.value?.quote_line_items.map(qli => {
    if (qli === updated_line_item) {
      const _set = formatQuoteLineItemForSave(
        updated_line_item,
        updated_line_item.quote_id
      );
      return {
        _set,
        where: {
          id: { _eq: updated_line_item.id }
        }
      };
    }
    return {
      _set: { sub_order: qli.sub_order },
      where: {
        id: { _eq: qli.id }
      }
    };
  });

  const quoteObj = formatQuoteHeaderForSave(quote?.value, advancedSearch?.value)

  const updatedLineItems = quote.value.quote_line_items
    .filter(lineItem => lineItem.id)
    .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
    .map(item => {
      return {
        _set: item,
        where: {
          id: { _eq: item.id }
        }
      };
    });

  return updateQuote({
    id: quote?.value?.id,
    object: quoteObj,
    create_line_items: [],
    update_line_items: updatedLineItems.concat(qlis),
  }).then((resp) => {
    toastMaterialReplaceSuccessMismatch(_selectedLineItemCopy, newLineItem);
    return resp;
  }).catch((err) => {
    return Promise.reject(new Error("Error trying to replace mismatched material types: ", err))
  })
}

const deleting = ref<boolean>(false);

const removeLineItem = (evt, quote_line_item) => {
  deleting.value = true;
  emit("toggle", deleting.value);

  const idx = quote_line_items.value.indexOf(quote_line_item);
  const removedQli = quote_line_items.value.at(idx);
  const qlisToRemove = [{
    _set: { deleted_at: "now()" },
    where: {
      id: { _eq: removedQli.id }
    }
  }]

  const quoteObj = formatQuoteHeaderForSave(quote?.value, advancedSearch?.value)
  const updatedLineItems = quote_line_items?.value
    .filter(lineItem => lineItem.id && lineItem.id !== removedQli.id)
    .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
    .map((item, index) => {
      return {
        _set: { ...item, sub_order: index + 1 },
        where: {
          id: { _eq: item.id }
        }
      };
    });

  return updateQuote({
    id: quote?.value?.id,
    object: quoteObj,
    create_line_items: [],
    update_line_items: updatedLineItems.concat(qlisToRemove)
  }).then((resp) => {
    return resp
  }).catch((err) => {
    console.error(
      "There was a problem deleting this line item:\n",
      quote_line_item,
      "\n",
      "Error provided:\n",
      err
    )
  }).finally(() => {
    props.refetchQuote().finally(() => {
      // Dev note: The deleted line item doesn't actually leave the UI until it is
      // done refetching. So keep inputs disabled until then to prevent race 
      // conditions. This also feels cleaner from a UI/UX standpoint in my opinion.
      deleting.value = false;
      emit("toggle", deleting.value);
    });
  });
};

// NOTE HELPERS:
// TODO: will move these note helpers into a separate vue template, similar to QuoteVersionDropDown.vue and ScenarioAutocomplete.vue
const toggleNoteEdit = (qli: any) => {
  // This field will be auto created if it does not already exist:
  qli.editing_note = !qli.editing_note;
};
const saveNote = (qli: any) => {
  toggleNoteEdit(qli);
};
const clearNote = (qli: any) => {
  qli.note = "";
  toggleNoteEdit(qli);
};

// DESCRIPTION HELPERS:
const toggleDescriptionEdit = (qli: any) => {
  qli.editing_description = !qli.editing_description
}

const saveDescription = (qli: any) => {
  toggleDescriptionEdit(qli);
}

const clearDescription = (qli: any) => {
  qli.erp_material_long_description = ''
  toggleDescriptionEdit(qli);
}

// SUBCATEGORY HELPERS:
const { data: equipmentTypeConversions } = useSkEquipmentTypeConversionsQuery({
  variables: { summitQueryString: "" }
});
const toggleSubcategory = (qli: any) => {
  if (qli.subcategory == SubCategory.Consumable) {
    qli.subcategory = SubCategory.Reusable;
  } else {
    qli.subcategory = SubCategory.Consumable;
  }
};
const isConsumable = (qli: any) => {
  return qli.subcategory === SubCategory.Consumable;
};
const isSerialized = (qli: any) => {
  return isQuantityOneMaterial(qli, equipmentTypeConversions.value?.getSKEquipmentTypeConversions || []);
};
const online = useOnline();

// ROW HELPERS:
const onRowReorder = async (event: DataTableRowReorderEvent) => {

  /**
   * This function recursively finds a table row element from a child element.
   * 
   * @param {HTMLElement} element - any nested element of PrimeVue's table row.
   * @returns - a table row element, or null if not found.
   * @remarks - due to us customizing (templating) beyond Primevue's default
   * DataTable rows/columns, this provides a reliable way to extract information
   * from the row element. 
   * @remarks - Primevue can attach nested/templated child elements to its drag
   * and drop event (as opposed to the row it was detected and dropped onto).
   */
  function _findParentTableRow(element) {
    if (!element) return null;
    if (element.tagName === 'TR') {
      return element;
    }

    if (element.parentElement) {
      return _findParentTableRow(element.parentElement);
    }

    return null;
  }

  const pickedUpItem = quote?.value?.quote_line_items[event.dragIndex];
  const droppedItem = quote?.value?.quote_line_items.find(qli =>
    /* Dev note: event.dropIndex (unreliably) accounts for top & bottom dragpoints,
    * so relying on that may result in inconsistent re-ordering.
    * TODO: Rework or further customize current row expansion method in the event
    * we want to leverage event.dropIndex. */
    qli.id === _findParentTableRow(event.originalEvent.target)?.id
  );
  const equipmentType1 = droppedItem?.line_item_type;
  const equipmentType2 = pickedUpItem?.line_item_type;

  if (!pickedUpItem || !droppedItem || saving.value) return;
  const originalGrouping = {
    line_item_type: pickedUpItem.line_item_type,
    parent_order: pickedUpItem.parent_order
  };

  const isDragDownReorder = pickedUpItem.sub_order < droppedItem.sub_order;
  const isDroppedItemLastItem =
    droppedItem.id === quote?.value?.quote_line_items.slice(-1)[0]?.id;

  const pickedUpItemNewSuborder =
    isDroppedItemLastItem || isDragDownReorder
      ? droppedItem.sub_order
      : droppedItem.sub_order + 1;

  const isEquipmentTypeMismatch = equipmentType1 !== equipmentType2;
  const isDroppedOnSameItem = pickedUpItem === droppedItem;
  const isDroppedOnSameSuborderAndType = (
    pickedUpItem?.sub_order === pickedUpItemNewSuborder &&
    equipmentType1 === equipmentType2
  );
  const invalidNumQlis = quote?.value?.quote_line_items?.length <= 1;

  if (isEquipmentTypeMismatch) {
    pickedUpItem.line_item_type = droppedItem?.line_item_type
    pickedUpItem.parent_order = droppedItem?.parent_order
  }

  if (isDroppedOnSameItem || isDroppedOnSameSuborderAndType || invalidNumQlis || !droppedItem) {
    return;
  }

  const saveReorderedLineItems = () => {
    const quoteObj = formatQuoteHeaderForSave(quote?.value, advancedSearch?.value);

    const updatedLineItems = structuredClone(toRaw(quote?.value?.quote_line_items))
      .filter(Boolean)
      .map(item => formatQuoteLineItemForSave(item, quote?.value?.id))
      .map(item => {
        // Must set this first for correct list order (to verify: remove this map)
        const isPickedUpItem = item.id === pickedUpItem.id;

        if (isPickedUpItem) {
          item.sub_order = pickedUpItemNewSuborder;
        }

        return item;
      })
      .sort((a, b) => { return a.sub_order - b.sub_order })
      .map((item, index) => {
        let subOrder = index + 1; // Passively recalculate sub_orders (relies on list order)
        const isPickedUpItem = item.id === pickedUpItem.id;
        const isDroppedOnItem = !isPickedUpItem && item.sub_order === pickedUpItemNewSuborder;

        if (isPickedUpItem) {
          // re-use calculated suborder for picked up item
          subOrder = pickedUpItemNewSuborder;
        }
        else if (isDroppedOnItem) {
          // actively recalculate item dropped on
          subOrder = isDragDownReorder
            ? pickedUpItemNewSuborder - 1 // edge case: push down instead of up
            : pickedUpItemNewSuborder + 1;
        }

        item.sub_order = subOrder;
        return {
          _set: item,
          where: {
            id: { _eq: item.id }
          }
        };
      });

    saving.value = true;
    emit("toggle", saving.value);
    return updateQuote({
      id: quote?.value?.id,
      object: quoteObj,
      create_line_items: [],
      update_line_items: updatedLineItems,
    });
  };

  let confirmed = false;
  try {
    const completion = Promise.resolve(saveReorderedLineItems())
      .then(resp => ({ error: resp?.error }), error => ({ error }));
    await haltUntilCompletion();
    if (!online.value) return;
    const { error } = await completion;
    if (error) throw error;
    confirmed = true;
    await props.refetchQuote();
    toastReorderSuccess(equipmentType1, pickedUpItem, droppedItem);
  } catch (error) {
    if (!confirmed) {
      Object.assign(pickedUpItem, originalGrouping);
    }
    toastReorderError(equipmentType1, error);
  } finally {
    saving.value = false;
    emit("toggle", saving.value);
  }
};

const onShowPriceHistory = (quote_line_items: any) => {
  emit('update:open-side-panel');
  emit("material-number-selected", quote_line_items?.erp_material_number);
}

const onRowSelect = (event: DataTableRowSelectEvent) => {
  selectedLineItemElement.value = event.originalEvent?.currentTarget;
  emit("update:open-side-panel");

  if (
    !selectedLineItem?.value?.material_number
  ) {
    // add to store
    setUserManualSearch(false);

    const description = selectedLineItem?.value?.erp_material_long_description;
    addSelectedDescription(description);
    emit("description-selected", description);

    addSelectedQuoteLineItem(selectedLineItem?.value);
  }
};

const onRowUnselect = (event: DataTableRowSelectEvent) => {
  selectedLineItemElement.value = Object.assign(
    selectedLineItemElement.value,
    null
  );

  removeSelectedDescription();
  removeSelectedQuoteLineItem();
  emit("description-selected", undefined);
};

/**
 * This function forces a row reorder. Use if Primevue's trigger is insufficient.
 *
 * @param {HTMLTableRowElement} row - ideally a table row (<tr>) element
 * @returns {Function} - the normal row reorder function
 * @remarks - the HTML event is mocked for modularity
 */
const forceRowReorder = (row: HTMLTableRowElement) => {
  const expandedRow = document.querySelector('[id^="row-expansion"]');
  const mockDragIndex = quote?.value?.quote_line_items.findIndex(qli =>
    qli.id === row.id
  );
  const mockOriginalEventTarget = document.getElementById(
    expandedRow.id.replace('row-expansion-', '')
  );

  const mockEvent = {
    dragIndex: mockDragIndex,
    originalEvent: {
      target: mockOriginalEventTarget
    }
  }

  return onRowReorder(mockEvent);
};

const forceExpandRow = (row_element: HTMLTableRowElement) => {
  const row_qli_id = row_element?.id;

  for (const qli_id in expandedRows.value) {
    if (qli_id !== row_qli_id) {
      delete expandedRows.value[qli_id];
    }
  }

  if (Object.keys(expandedRows.value).includes(row_qli_id)) {
    return; // Dev note: for performance reasons, keep this here
  }
  else {
    expandedRows.value[row_qli_id] = true;
  }
  expandedRows.value = { ...expandedRows.value };
};

const forceCollapseAllRows = () => {
  for (const row_qli_id in expandedRows.value) {
    delete expandedRows.value[row_qli_id]; // Could also set them to false, but deleting works too
  }
  expandedRows.value = { ...expandedRows.value };
};

// TOAST HELPERS (TODO: will create a new toastHelpers.ts file, so other files can use these):
const toastReorderError = (equipmentType: string, error: string | unknown) => {
  const summary = "There was a problem reordering " + equipmentType + "s.";
  const detail =
    "Please contact support." +
    (error ?? "\n\nOptionally provide this error message:\n" + error);
  toast.add({
    severity: "error",
    summary,
    detail,
    life: 7000
  });
};

const toastMaterialAddSuccess = (newMaterial: any) => {
  const summary = "New " + newMaterial.line_item_type + " added";
  const detail =
    newMaterial.erp_material_long_description +
    "\n\nNew position: " +
    newMaterial.sub_order * 10;
  toast.add({
    severity: "success",
    summary,
    detail,
    life: 7000
  });
};

const toastMaterialAddSerializedSuccess = (newMaterial: any, numberAdded: number, suborderStart: number, suborderEnd: number) => {
  const summary = `${numberAdded} ${newMaterial.line_item_type}'s added`;
  const detail =
    newMaterial.erp_material_long_description +
    "\n\nNew positions: " +
    suborderStart * 10 + " to " + suborderEnd * 10;
  toast.add({
    severity: "success",
    summary,
    detail,
    life: 7000
  });
};

const toastMaterialAddError = (newMaterial: any, error: string) => {
  const summary =
    "There was a problem adding this " + newMaterial?.equipmentType + "s.";
  const detail =
    "Please contact support.\n\nOptionally provide this error message:\n" +
    error;
  toast.add({
    severity: "error",
    summary,
    detail,
    life: 7000
  });
};

const toastMaterialReplaceSuccess = (
  newMaterial: any
) => {
  const summary = newMaterial.line_item_type + " replaced";
  const detail =
    "New " +
    newMaterial.line_item_type +
    ":\n" +
    newMaterial.erp_material_long_description +
    "\n\nReplaced material: " +
    newMaterial.sub_order * 10;
  toast.add({
    severity: "success",
    summary,
    detail,
    life: 10000
  });
};

const toastMaterialReplaceSuccessMismatch = (
  selectedLineItemCopy: any,
  selectedMaterial: any
) => {
  const summary =
    "Replaced " +
    selectedLineItemCopy.line_item_type +
    " with " +
    selectedMaterial.line_item_type;
  const detail =
    "Old: " +
    selectedLineItemCopy.erp_material_long_description +
    ":\n\n" +
    "New: " +
    selectedMaterial.erp_material_long_description +
    "\n\n" +
    "Updated position: " + (selectedLineItem.value.sub_order * 10)
  toast.add({
    severity: "success",
    summary,
    detail,
    life: 7000
  });
};

const toastMaterialReplaceError = (error: any) => {
  const summary = "There was a problem replacing materials";
  const detail =
    "Please contact support.\n\nOptionally provide this error message:\n" +
    error;
  toast.add({
    severity: "error",
    summary,
    detail,
    life: 7000
  });
};

const toastReorderSuccess = (
  equipmentType: string,
  selectedItem: any,
  reorderedItem: any
) => {
  const summary = "Reordered " + equipmentType + "'s";
  const detail =
    "Selected item:\n" +
    selectedItem?.erp_material_long_description +
    "\n\nReordered with:\n" +
    reorderedItem?.erp_material_long_description;
  toast.add({ severity: "info", summary, detail, life: 7000 });
};

const toastMarginApplied = (
  margin: string,
  old_price: any,
  new_price: any,
  position: any
) => {
  const summary = margin + "% margin applied.";
  const detail =
    "Old price: " +
    formatCurrency(old_price, quote?.value?.currency) +
    "\nNew price: " +
    formatCurrency(new_price, quote?.value?.currency) +
    "\nPosition: " +
    position * 10;
  toast.add({ severity: "info", summary, detail, life: 7000 });
};

// BANDAID FIXES:
const isCustomLineItem = (quote_line_item: any) => {
  // TODO: we can later refine how we want to identify a custom line item,
  // they need to display differently then regular line items
  // I'm opting to check against an MN of 'custom', currently the fastest way given current time constraints

  return quote_line_item.erp_material_number == 'custom'
}

function applyDragoverFunctions() {
  // No easy/default way to target (or template) dynamically generated rows from Primevue's DataTable,
  // unless it's: 1) via JavaScript or 2) Primevue's Pass Through options
  //
  // Opted to attach the event through JavaScript because it's less expensive. Pass Through options re-renders
  // the UI element several times, and causes the attached event function to trigger automatically
  // during those re-renders (the event is not manually triggered by the user).
  //
  // TODO: attach the ondragenter event on the columns individually instead?
  const rows = document.querySelectorAll(
    "table tbody .p-selectable-row, table tbody .p-rowgroup-header"
  );

  for (const row of rows) {
    const _row = row as HTMLTableRowElement;
    _row.ondragover = function () {
      forceExpandRow(_row);
    };
    _row.ondragend = function () {
      const isDroppedOnExpansionRow = document.querySelector('[id^="row-expansion"]')?.matches(':hover')
      if (isDroppedOnExpansionRow) {
        // Not triggered by Primevue in the scenario, so force it
        forceRowReorder(_row);
      }
      forceCollapseAllRows();
    };
  }
}

// PRICE:
const recalcMarginFromPriceOverride = (event, quote_line_item) => {
  if (isQuoteAdjustmentLineItem(quote_line_item)) {
    quote_line_item.price_override = getQuoteLineUnitPrice(quote_line_item);
  }
  /* hasBasePrice: we need to check if users have defined a price for their material
     hasZeroForPriceOverride: whether users enter 0 or fully backspace, it's still 0
  ***/
  const hasBasePrice = quote_line_item.erp_price && quote_line_item.erp_price != 0
  const hasZeroForPriceOverride = !Number(quote_line_item.price_override)

  if (!hasBasePrice) {
    // When ERP prices come back as 0, there is no base price to derive the margin?
    quote_line_item.margin = quote_line_item.margin;
  }
  else if (hasZeroForPriceOverride) {
    // Cannot divide by 0, so the margin by extension is 0 (unless we want to save NaN or Infinity to hasura)
    quote_line_item.margin = 0;
  }
  else {
    // Regular margin calculation
    quote_line_item.margin = (((quote_line_item.price_override - quote_line_item.erp_price) / quote_line_item.price_override) * 100).toFixed(3)
  }
}

function recalcPriceOverrideFromMargin(event, quote_line_item, debounce=true, save=true) {
  // TODO: Had to change this to a function for order of operations reason.
  //       Change this back to a const for pattern reasons?
  /* hasBasePrice: we need to check if users have defined a price for their material
     hasZeroForMargin: whether users enter 0 or fully backspace, it's still 0
     hasInvalidMargin: for when margins are >= 0, a different formula is needed
  ***/
  const hasBasePrice = quote_line_item.erp_price && quote_line_item.erp_price != 0
  const hasZeroForMargin = !quote_line_item.margin
  const hasInvalidMargin = quote_line_item.margin && quote_line_item.margin >= 100

  if (hasBasePrice) {
    /* Only calculate the unit price if there is an erp_price.
       If there is no ERP price, (which I also refer to as base price),
       there is no base price to derive the price override (also referred to
       as the selling price). In that case, if no price override can be derived,
       the automatic calculation will be skipped, as it cannot be calculated.
    ***/
    if (hasZeroForMargin) {
      // Reset to erp_price when the margin is zeroed (users enter 0 or backspace)
      quote_line_item.price_override = quote_line_item.erp_price
    }
    else if (hasInvalidMargin) {
      /* Technically not practical in most business contexts, but as a temporary
         solution, we'll continue to use the regular selling price formula

         In this scenario, we are either dividing by 0, or dividing by a negative.

         There are 3 options in this situation:
         1) Continue showing the selling price at margins >= 100, even if invalid
            (^ currently doing this)
         2) Block the UI from entering margins >= 100
            (^ examples: disabling the input, showing pop up warning, highlighting
            border in red. The only consideration with highlighting the border in
            red is that inputting a price override can lead to 100% margin.
            The formula in that scenario won't lead to anything greater than 100%,
            so there'd need to be some careful consideration with this option)
         3) If the business insists on showing markup as a re-interpreation of
            division by 0 or division by a negative, here is the JavaScript formula:

            const multiplier = 1 + (quote_line_item.margin / 100);
            quote_line_item.price_override =
              (quote_line_item.erp_price * multiplier).toFixed(2);
      ***/
      quote_line_item.price_override = (quote_line_item.erp_price / (1 - (quote_line_item.margin / 100))).toFixed(2);
      if (quote_line_item.price_override == Infinity) {
        // Set to 0, as we do not want to save Infinity to hasura, but negatives are fine, even if it's not a valid selling price
        quote_line_item.price_override = 0
      }
    }
    else {
      // Regular selling price calculation
      quote_line_item.price_override = (quote_line_item.erp_price / (1 - (quote_line_item.margin / 100))).toFixed(2);
    }
  }
}

const recalcPriceOverrideAndMarginFromTotal = (event, quote_line_item) => {
  // The total is not a v-model so I'm using this to extract its value:
  const total = Number(event?.target?.value); // TODO: check edge cases (1, 0, etc)
  // 0s and negatives are invalid for quantity. Fallback to 1 if they get entered:
  const quantity = quote_line_item.quantity > 0 ? quote_line_item.quantity : 1;
  /* Derive the price override with the ensured quantity:

     Our price override is also referred to as the "selling price"
     in business contexts. "Selling unit price" would also makes sense.
  ***/
  quote_line_item.price_override = (total / quantity).toFixed(2)
  /* Next, recalculate the margin based on the newly calculated selling price:

  Note that decimal precision *may* be a problem when deriving the margin,
  SPECIFICALLY from this point. I tried assigning the non-toFixed() to
  qli.price_override first, and then doing toFixed after, but it's causing
  the decimal points to be dropped in the total box.

  Also if users want a negative total, they'll have to input it twice.
  The first '-' is getting dropped at some point with this function,
  not sure when. But, given the context of the app this is low priority.

  If this is ever an issue we may want total to be a v-model.
  ***/

  recalcMarginFromPriceOverride(event, quote_line_item);
};

// For date selection
const queryParams = reactive({
  expand: "pricing",
  deleted: "False"
})

const {
  data: skMaterials,
  executeQuery: fetchMaterials,
  fetching,
  isPaused
} = useSkMaterialsQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    if (!queryParams?.number) {
      return {};
    }
    return {
      summitQueryString: createQueryString(queryParams)
    };
  })
});

const recalculateUnitPriceAndTotalFromDate = (quote_line_item: any) => {
  queryParams.number = quote_line_item.erp_material_number;
  queryParams.contract = quote?.value?.erp_contract_number || null;
  queryParams.price_list = quote?.value?.erp_price_list_number || null;
  queryParams.pricing_date = fixDate(quote_line_item.pricing_date);
  queryParams.plant = quote?.value?.erp_plant_id
  queryParams.country = quote?.value?.erp_well_country

  /* Dev note: contract isn't "required", and pricing_date is being read from
  the quote line item object, and not the parent quote object.  */
  const missingRequiredFields = [
    queryParams.price_list,
    queryParams.country,
    queryParams.plant
  ].some(field => !field)

  if (missingRequiredFields && !showedPriceDateWarnToast.value) {
    toast.add({
      severity: 'warn',
      summary: 'Missing header data',
      detail: 'For accurate cost updates, please select and save required header fields.',
      life: 7000
    });
    showedPriceDateWarnToast.value = true;
  }

  nextTick()
    .then(fetchMaterials)
    .then(resp => {
      quote_line_item.erp_price = Number(resp?.data?.value?.getSKMaterials[0]?.pricing?.price) || 0.00;

      if (quote_line_item.margin && quote_line_item.margin != 0) {
        recalcPriceOverrideFromMargin(null, quote_line_item, false)
      }
      else {
        quote_line_item.price_override = Number(resp?.data?.value?.getSKMaterials[0]?.pricing?.price)
      }
    });
}

// OTHER:
const quoteIsWonOrRejected = computed(() => {
  if (
    [statuses.won, statuses.rejected].some(
      status => status === quote?.value?.status
    )
  ) {
    selectedLineItem.value = null; // Force de-select a line item if selected and transitioned to won / rejected
    return true;
  }
  return false;
});

const _isJobTypeWellTestRental = computed(() => {
  return isJobTypeWellTestRental(quote?.value?.sk_job_type)
})

const checkValidInputWrapper = (
  event: KeyboardEvent,
  allowDecimals: boolean = true,
  allowNegatives: boolean = true,
  recalcFunction: Function = null
) => {
  // A wrapper for checkValidNumberInput, as a catch all for 1) input sanitization and
  // 2) allowing recalculations when 'Enter' is pressed while an input is focused.
  // This could be renamed but this will work for now.
  const isValidInput = checkValidNumberInput(event, allowDecimals, allowNegatives); // <--- prevents event
  const isEnterKey = event.key === 'Enter';
  const devPassedRecalcFunction = !!recalcFunction
  if (isValidInput && isEnterKey && devPassedRecalcFunction) {
    recalcFunction();
  }
}

const copyDescriptionToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.add({
      severity: 'success',
      summary: 'Copied to clipboard',
      detail: 'Description has been copied to your clipboard',
      life: 2000
    });
  }).catch((_err) => {
    toast.add({
      severity: 'error',
      summary: 'Failed to copy',
      detail: 'Could not copy description to clipboard',
      life: 2000
    });
  });
};

const handleCopyButtonClick = (event: MouseEvent, text: string) => {
  const selection = window.getSelection();
  const highlightingForCopyPaste = (selection && selection.toString().length > 0);
  if (highlightingForCopyPaste) {
    event.stopPropagation();
    event.preventDefault();
  }
};

onMounted(() => {
  watch(quote_line_items, (newQlis) => {
    if (newQlis) {
      applyDragoverFunctions();
      loading.value = false;
    }
  });
});
</script>

<style lang="scss" scoped>
.loading-spinner {
  border: 2px solid $spyglass-blue; /* Light grey */
  border-top: 2px solid var(--surface-400); /* Blue */
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.pi.pi-trash {
  cursor: pointer;
}
</style>

<style lang="scss">
#quotesDataTable.p-datatable .p-datatable-header {
  /** QUOTE DESIGN LIST (removing default/inherited backgrounds
    so it inherits the background gradiant image) */
  background: none;
  border: none;
  padding: 0;
}

#quotesDataTable input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

#quotesDataTable .p-component-overlay {
  // Primevue's "loading" attribute on the DataTable component is just a separate overlay on top of it.
  // This class is overwriting its default values for a more pleasant UI experience

  top: 130px;
  width: 100%; // extends full width of the table width
  height: 55%; // extends over the default blank table height
  color: var(--surface-600);
  background-color: var(--gray-200);
  border: black 2px solid; // original overlay is transparent - I removed the transparency because it didn't look right
}

#quotesDataTable .input-symbol-usd {
  cursor: text;
  position: relative;
}
#quotesDataTable .input-symbol-usd input {
  padding-left: 16px;
}
#quotesDataTable .input-symbol-usd:before {
  position: absolute;
  top: 0px;
  left: 8px;
  content: "$";
  color: var(--text-color); // Prevents row select from inverting the color
  pointer-events: none; // Allows selection of the underlying primevue component
}

#quotesDataTable .input-symbol-percentage {
  cursor: text;
  position: relative;
}
#quotesDataTable .input-symbol-percentage input {
  padding-right: 16px;
}
#quotesDataTable .input-symbol-percentage:after {
  position: absolute;
  top: 0px;
  left: 63px;
  content: "%";
  color: var(--text-color); // Prevents row select from inverting the color
  pointer-events: none; // Allows selection of the underlying primevue component
}

#quotesDataTable .icon-height-fix {
  padding: 0px;
  margin-bottom: 8px;
  height: 16.67px;
  // ^ the standard height of a regular div, the icon is expanding it to 19px but doesn't need to be that high
}

/* The following undoes some styling provided by p-inputtext, such that we are able to have
the functionality of the Calendar component *and* have the input/text styling desired by Ian */
// TODO: the following specificity rules might need work
#quotesDataTable span#pricing-date {
  border-bottom: 2px dotted var(--text-color) !important;
  display: inline-block; // Remove display style when flicker issue resolved

  &:hover + span.pricing-date-temporary-fix:not(.p-disabled) {
    // This block can be removed when flicker issue is resolved
    color: var(--info-500);
    transition: color 0.3s ease;
    cursor: pointer;
  }
}

#quotesDataTable .pricing-date-temporary-fix {
  // This class can be removed when flicker issue is resolved
  position: absolute;
  top: 8px;
  left: 0;
  font-size: 1rem; // matches calendar input text font size
  pointer-events: none; // allow calendar input click
}

#quotesDataTable #pricing-date input.p-inputtext.p-component:hover {
  color: transparent; // Restore to var(--info-500) when flicker issue resolved
  cursor: pointer;
}

#quotesDataTable #pricing-date button.p-button.p-component.p-button-icon-only.p-datepicker-trigger:hover {
  color: var(--info-500);
  cursor: pointer;
}

#quotesDataTable #pricing-date input.p-inputtext.p-component {
  border: 0px;
  background: none;
  box-shadow: none;
  padding-bottom: 0px;
  height: 100%;
  padding-left: 0;
  padding-right: 0;
  width: 6.5rem;
  color: transparent; // Restore to var(--info-500) when flicker issue resolved
}

#quotesDataTable #pricing-date button.p-button.p-component.p-button-icon-only {
  border: 0px;
  background: none;
  box-shadow: none;
  padding-bottom: 0px;
  width: 16px;
}
// End undo styling changes

#quotesDataTable thead.p-datatable-thead tr > th {
  /** Main header */
  border-bottom: black 2px solid;
}

#quotesDataTable tr.p-rowgroup-header {
  /** Subheader row */
  background-color: var(--surface-300);
  border: black 2px solid;
  font-weight: bold;
  padding-top: 0.5rem;
}

#quotesDataTable tbody.p-datatable-tbody tr > td {
  /** Subheader padding */
  padding-top: 0.3em;
  padding-bottom: 0.3em;
}

#quotesDataTable tbody.p-datatable-tbody tr {
  /* Data rows */
  // For transition animation will apply to both selecting and unselecting line item rows
  transition: linear;
  -webkit-transition: 100ms linear;

  .data-column {
    padding-left: 0.5em;
    padding-right: 0.5em;
    padding-bottom: 1em;
    padding-top: 1em;
  } 
}

#quotesDataTable tbody.p-datatable-tbody tr {
  .line-item-button {
    margin-left: 0.5rem;
    background: transparent;
    box-shadow: none;
  
    &:hover {
      background: var(--surface-200);
      border-radius: 4px;
    }
  }
}

#quotesDataTable .dndPlaceholder {
  // The 'Drop Material Here' part when drag and dropping
  vertical-align: center;
  padding: 0.75rem 1.25rem;
  border: 1px dashed #598af1;
  border-radius: 4px;
  -webkit-border-radius: 4px;
  color: #598af1;
}

#quotesDataTable tr.p-datatable-dragpoint-bottom {
  /* Prefixed with tr to target the type category headers */
  > td {
    /* Styling on the column level seems to work better than on a row level */
    box-shadow: none; // Overwrites the default Primevue shadow, not needed
  }
}

#quotesDataTable .link-button {
  /* The following undoes styles provided by button or primevue's Button */
  text-align: left;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  font: inherit;
  transition: color 0.3s ease, text-decoration 0.3s ease;
}

#quotesDataTable .link-button:hover {
  color: var(--info-500); /* Change color on hover */
  text-decoration: none;
}

#quotesDataTable .link-button-disabled {
  /* The following undoes styles provided by button or primevue's Button */
  text-align: left;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
}

// Row styles:
.mn-badge {
  border: 1px solid black;
  border-radius: 5px;
  background-color: black;
  color: white;
  padding-bottom: 3px;
  padding-left: 6px;
  padding-right: 6px;
  white-space: nowrap;
}
.dollar-icon {
  border-radius: 20px;
  background-color: #51be6f;
  color: white;
  font-weight: bold;
  padding: 4px;
  color: white;
}
.ban-icon {
  padding: 4px;
  font-size: 1em;
  font-weight: bold;
}

.consumable-text {
  color: #51be6f; // TODO: check vars so that link will highlight in blue on hover and not green
  font-weight: bold;
  text-align: left;
}

.non-consumable-text {
  font-weight: bold;
  text-align: left;
}

.product-badge {
  background-color: #828282;
  color: white;
  font-weight: bold;
}

// Header styles:
#quotesDataTable tr {
}

#quotesDataTable .empty {
  background-color: #dee8fc;
  border: 3px solid #d1defb;
  border-radius: 0.18rem;
}
.note-button-add-1 {
  border: 1px solid black;
  display: inline-block;
}

.note-button-add-2 {
  padding-top: 0px;
  padding-bottom: 0px;
  vertical-align: bottom;
}

.note-button-save {
  padding-top: 2px;
  padding-bottom: 2px;
  vertical-align: bottom;
  float: left;
}

.note-button-clear {
  padding-top: 2px;
  padding-bottom: 2px;
  vertical-align: bottom;
  float: right;
}

.pricing-date {
  margin-left: 0px;
  margin-right: 0px;
  padding-left: 0px;
  padding-right: 0px;
  padding-bottom: 0px;
}

.trash-icon {
  padding: 0px;
  margin: 0px;
  width: 16px;
}

.dnd-handle {
  justify-content: center;
  display: flex;
  width: 100%;
  // Width: 100% further the extends the user-draggable area on the left and right side (for a better user experience)
  // Vertical alignment is needed because the row identifer numbers dynamically extend the width of the DnD column
}

.line-item-note-area {
  resize: none;
}
</style>
